import React, { useState, useRef, useEffect, useCallback } from 'react';
import { 
  Box, 
  TextField, 
  List, 
  ListItem, 
  ListItemText, 
  CircularProgress, 
  IconButton,
  Typography,
  ClickAwayListener,
  Popper,
  Paper
} from '@mui/material';
import { Search, Clear, KeyboardArrowDown } from '@mui/icons-material';
import { useInfiniteScrollSearchableSelectStyles } from './useInfiniteScrollSearchableSelectStyles';

const InfiniteScrollSearchableSelect = ({
  label,
  placeholder = 'Select an option...',
  value,
  onChange,
  onSearch,
  options = [],
  loading = false,
  loadingMore = false,
  hasMore = false,
  error,
  helperText,
  disabled = false,
  required = false,
  clearable = true,
  searchable = true,
  maxHeight = 240,
  className,
  style,
  displayKey = 'label',
  valueKey = 'value',
  searchKey = 'label',
}) => {
  const styles = useInfiniteScrollSearchableSelectStyles();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOption, setSelectedOption] = useState(null);
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const observerRef = useRef(null);
  const previousSearchTermRef = useRef('');
  const isInitialLoadRef = useRef(false);
  const isScrollLoadingRef = useRef(false);

  // Update selected option when value changes
  useEffect(() => {
    if (value) {
      const option = options.find(opt => opt[valueKey] === value);
      setSelectedOption(option || null);
    } else {
      setSelectedOption(null);
    }
  }, [value, options, valueKey]);

  // Handle search with debouncing
  useEffect(() => {
    // Prevent duplicate calls for the same search term
    if (searchTerm === previousSearchTermRef.current) {
      return;
    }

    const timeoutId = setTimeout(() => {
      if (onSearch) {
        if (searchTerm.trim() !== '') {
          previousSearchTermRef.current = searchTerm;
          onSearch(searchTerm);
        } else {
          previousSearchTermRef.current = searchTerm;
          onSearch('');
        }
      }
    }, 800); // Debounce time for API calls

    return () => clearTimeout(timeoutId);
  }, [searchTerm, onSearch]); // Removed options.length dependency

  // Reset scroll loading flag when loading state changes
  useEffect(() => {
    if (!loadingMore) {
      isScrollLoadingRef.current = false;
    }
  }, [loadingMore]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isScrollLoadingRef.current = false;
      previousSearchTermRef.current = '';
      isInitialLoadRef.current = false;
    };
  }, []);

    // Intersection observer for infinite scroll
  const lastOptionRef = useCallback(node => {
    
    if (loadingMore) return;
    
    if (observerRef.current) observerRef.current.disconnect();
    
    observerRef.current = new IntersectionObserver(entries => {
      
      if (entries[0].isIntersecting && hasMore && !loadingMore) {
        onSearch?.(searchTerm, true);
      }
    }, {
      root: null,
      rootMargin: '20px',
      threshold: 0.1
    });
    
    if (node) observerRef.current.observe(node);
  }, [loadingMore, hasMore, onSearch, searchTerm]);

  const handleToggle = () => {
    if (disabled) return;
    
    if (!isOpen) {
      setIsOpen(true);
      // Load all options when dropdown opens for the first time
      if (onSearch && options?.length === 0) {
        onSearch('');
      }
      // Focus search input after dropdown opens
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 100);
    } else {
      setIsOpen(false);
    }
  };

  const handleOptionSelect = (option) => {
    setSelectedOption(option);
    onChange?.(option[valueKey], option);
    setIsOpen(false);
    // Don't clear search term - keep it for continued searching
    // setSearchTerm('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setSelectedOption(null);
    onChange?.(null, null);
    setSearchTerm('');
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleClickAway = () => {
    setIsOpen(false);
    setSearchTerm('');
    // Reset flags when dropdown closes
    isScrollLoadingRef.current = false;
    previousSearchTermRef.current = '';
    isInitialLoadRef.current = false; // Reset to allow fresh load when reopened
  };

  // Reset scroll loading flag when loadingMore changes
  useEffect(() => {
    if (!loadingMore) {
      isScrollLoadingRef.current = false;
    }
  }, [loadingMore]);

  const getDisplayText = () => {
    if (selectedOption) {
      return selectedOption[displayKey] || 'Unknown option';
    }
    return placeholder;
  };

  const isError = !!error || !!helperText;

  return (
    <Box sx={styles.container} className={className} style={style}>
      {/* Label */}
      {label && (
        <Typography sx={styles.label}>
          {label}
          {required && <span className="required"> *</span>}
        </Typography>
      )}

      {/* Trigger */}
      <Box
        ref={triggerRef}
        sx={[
          styles.trigger,
          isError && styles.errorTrigger,
          disabled && styles.disabledTrigger,
        ]}
        onClick={handleToggle}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={label || 'Select option'}
      >
        <Box sx={styles.triggerContent}>
          <Typography
            sx={[
              styles.displayText,
              selectedOption && { opacity: 1 }
            ]}
            className={selectedOption ? 'has-value' : ''}
          >
            {getDisplayText()}
          </Typography>
        </Box>

        <Box sx={styles.triggerActions}>
          {clearable && selectedOption && !disabled && (
            <IconButton
              size="small"
              onClick={handleClear}
              sx={styles.clearButton}
              aria-label="Clear selection"
            >
              <Clear sx={{ fontSize: '16px' }} />
            </IconButton>
          )}
          <KeyboardArrowDown
            sx={[
              styles.arrowIcon,
              isOpen && { transform: 'rotate(180deg)' }
            ]}
          />
        </Box>
      </Box>

      {/* Dropdown */}
      <Popper
        open={isOpen}
        anchorEl={triggerRef.current}
        placement="bottom-start"
        style={{ zIndex: 1300 }}
        ref={dropdownRef}
        modifiers={[
          {
            name: 'sameWidth',
            enabled: true,
            fn: ({ state }) => {
              state.styles.popper.width = `${state.rects.reference.width}px`;
            },
            phase: 'beforeWrite',
            requires: ['computeStyles'],
          },
        ]}
      >
        <ClickAwayListener onClickAway={handleClickAway}>
          <Paper sx={[styles.dropdown, { maxHeight: `${maxHeight}px` }]} elevation={8}>
            {/* Search Input */}
            {searchable && (
              <Box sx={styles.searchContainer}>
                <TextField
                  ref={searchInputRef}
                  fullWidth
                  size="small"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: <Search sx={styles.searchIcon} />,
                  }}
                  sx={styles.searchInput}
                  variant="outlined"
                />
              </Box>
            )}

            {/* Options List */}
            <List 
              sx={styles.optionsList}
              onScroll={(e) => {
                const { scrollTop, scrollHeight, clientHeight } = e.target;
                const isNearBottom = scrollTop + clientHeight >= scrollHeight - 20;
                
                if (isNearBottom && hasMore && !loadingMore && !isScrollLoadingRef.current) {
                  isScrollLoadingRef.current = true;
                  onSearch?.(searchTerm, true);
                }
              }}
            >
              {loading ? (
                <Box sx={styles.loadingContainer}>
                  <CircularProgress size={20} />
                  <Typography sx={styles.loadingText}>
                    Loading options...
                  </Typography>
                </Box>
              ) : options?.length === 0 ? (
                <Box sx={styles.emptyContainer}>
                  <Typography sx={styles.emptyText}>
                    {searchTerm ? 'No cities found.' : 'No cities found.'}
                  </Typography>
                </Box>
              ) : (
                options?.map((option, index) => {
                  const isSelected = selectedOption?.[valueKey] === option[valueKey];
                  const isLast = index === options?.length - 1;
                  // Ensure unique key by using id if available, otherwise combine multiple fields with index
                  const key = option.id || `${option[valueKey] || option[displayKey] || 'unknown'}-${index}-${option.state || option.country || 'unknown'}`;
                  
                  return (
                    <ListItem
                      key={key}
                      sx={[
                        styles.optionItem,
                        isSelected && styles.selectedOption,
                      ]}
                      onClick={() => handleOptionSelect(option)}
                      ref={isLast ? lastOptionRef : null}
                      role="option"
                      aria-selected={isSelected}
                    >
                      <ListItemText
                        primary={option[displayKey] || 'Unknown option'}
                        sx={styles.optionText}
                      />
                    </ListItem>
                  );
                })
              )}

              {/* Loading More Indicator */}
              {loadingMore && (
                <Box sx={styles.loadingMoreContainer}>
                  <CircularProgress size={16} />
                  <Typography sx={styles.loadingMoreText}>
                    Loading more...
                  </Typography>
                </Box>
              )}
              
              {/* Manual Load More Button for Testing */}
              {hasMore && !loadingMore && options?.length > 0 && (
                <ListItem
                  key="load-more-button"
                  sx={{
                    ...styles.optionItem,
                    justifyContent: 'center',
                    backgroundColor: '#f5f5f5',
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: '#e0e0e0',
                    }
                  }}
                  onClick={() => {
                    if (!isScrollLoadingRef.current) {
                      isScrollLoadingRef.current = true;
                      onSearch?.(searchTerm, true);
                    }
                  }}
                >
                  <Typography sx={{ fontSize: '12px', color: '#666' }}>
                    Click to load more...
                  </Typography>
                </ListItem>
              )}
            </List>
          </Paper>
        </ClickAwayListener>
      </Popper>

      {/* Helper Text */}
      {(helperText || error) && (
        <Typography
          sx={[
            styles.helperText,
            (error || helperText) && styles.errorText
          ]}
        >
          {error || helperText}
        </Typography>
      )}
    </Box>
  );
};

export default InfiniteScrollSearchableSelect; 